import { useEffect, useState } from 'react';
import type { Agent } from 'src/data/models/agent.model';
import type { Pagination } from 'src/data/models/pagination.model';
import { agentRemotedata } from 'src/data/network/remote-data/agent-remote-data';
import { copyWith } from 'src/utils/general_helper';
import type { IPaginationData } from './pagination-data.interface';
import type { PaginationDataProps } from './pagination-data.props';

interface Params {
  limit?: number;
  skip?: number;
  cityName?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
}

const defaultParams: Params = {
  skip: 1,
  limit: 250,
  cityName: undefined,
  search: undefined,
  sortBy: 'name',
  sortOrder: 'asc',
};

export function useAgentPagination(
  props?: PaginationDataProps<Params>
): IPaginationData<Agent, Params> {
  const [params, setParams] = useState<Params>(
    copyWith<Params>(defaultParams, props?.params ?? {})
  );
  const [hasMaxReach, setHasMaxReach] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initial, setInitial] = useState(false);
  const [pagination, setPagination] = useState<Pagination<Agent> | null>(null);
  const [data, setData] = useState<Agent[]>([]);

  const updateParams = (newParams: Params) => {
    setParams(copyWith<Params>(params, newParams));
  };

  const updatePage = (page: number) => {
    updateParams({ skip: page });
  };

  const clear = () => {
    setData([]);
  };

  const refresh = () => {
    updateParams({ skip: 1 });
  };

  const loadData = async (args?: { refresh?: boolean }) => {
    setLoading(true);
    const response = await agentRemotedata.getAgents(params);
    setLoading(false);
    setPagination(response);
    if (props?.infiniteData ?? false) {
      setData((prev) => [...prev, ...response.items]);
      if (response.items.length < (params.limit ?? 0)) {
        setHasMaxReach(true);
      }
    } else {
      setData(response.items);
    }
  };

  const nextPage = async () => {
    if (!hasMaxReach) {
      if (pagination?.currentPage) {
        updatePage(pagination?.currentPage + 1);
      }
    }
  };

  useEffect(() => {
    setInitial(true);
    if (props?.loadingOnInit ?? false) {
      refresh();
    }
  }, []);

  useEffect(() => {
    if (initial) {
      setHasMaxReach(false);
      loadData();
    }
  }, [params]);

  return {
    params,
    pagination,
    data,
    hasMaxReach,
    loading,
    nextPage,
    clear,
    refresh,
    updatePage,
    updateParams,
    loadData,
  };
}
